import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Formik, Form } from 'formik';
import { loginSchema } from './validation';
import Layout from '../../layout/Layout';
import { TextForm, Password, Delimeter, ValidationBlock, GenericFields } from 'components/atoms';
import FormLayout from 'layout/FormLayout';
import { useActions } from 'hooks/useActions';
import { useTypedSelector } from 'hooks/useTypedSelector';
import { withGoogleReCaptcha } from 'hoc/withGoogleReCaptcha';
import { GOOGLE_AUTH_LINK } from 'utils/constants';

const formFields = [
  {
    name: 'email',
    label: 'EMAIL',
    Component: TextForm,
    type: 'email',
    autoFocus: true,
  },
  {
    name: 'password',
    label: 'PASSWORD',
    Component: Password,
  },
];

const Component: React.FC = () => {
  const [validate, setValidate] = React.useState(false);
  const { isAuthenticated } = useTypedSelector((state) => state.auth);
  const { loginUserWithEmail } = useActions();
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();

  React.useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    if (isAuthenticated) {
      timeoutId = setTimeout(() => {
        navigate('/');
      }, 500);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isAuthenticated, navigate]);

  return (
    <Layout>
      <FormLayout>
        <Formik
          initialValues={{
            email: '',
            password: '',
          }}
          validationSchema={loginSchema}
          validateOnChange={validate}
          validateOnBlur={false}
          onSubmit={async (values) => {
            setValidate(true);
            if (!executeRecaptcha) {
              return;
            }
            const token = await executeRecaptcha('login');
            loginUserWithEmail({ ...values, token }, navigate);
          }}
        >
          {(props) => (
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                setValidate(true);
                props.handleSubmit(e);
              }}
              noValidate
            >
              <div>
                <div className="field-list">
                  <h2 className="login-title">Sign in</h2>
                </div>
                <ValidationBlock />
                <GenericFields fields={formFields} submitText="Sign in" />
                <Delimeter />
                <div className="field-list separator">
                  <div className="data-name">
                    <p>
                      <span onClick={() => navigate('/users/forgot')}>Forgot?</span>
                    </p>
                  </div>
                  <div className="data-name right">
                    <p>
                      Don't have an account yet? Register{' '}
                      <span onClick={() => navigate('/users/register')}>now</span>
                    </p>
                  </div>
                </div>
                <div className="field-list separator" style={{ justifyContent: 'center', padding: '15px' }}>
                  <a
                    href={GOOGLE_AUTH_LINK}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '10px',
                      color: '#000',
                      textDecoration: 'none',
                      fontWeight: 500,
                      fontSize: '14px',
                      letterSpacing: '0.5px',
                      border: '1px solid #000',
                      padding: '8px 16px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                      <path fill="none" d="M0 0h48v48H0z"/>
                    </svg>
                    Sign in with Google
                  </a>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </FormLayout>
    </Layout>
  );
};

export const Login = withGoogleReCaptcha(Component);
